import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Check, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

interface QuoteLabelInlineEditProps {
  quotationId: string;
  /** Manual override currently stored on the quote */
  value?: string | null;
  /** Auto-generated label, used as the placeholder / fallback display */
  autoLabel: string;
  invalidateKeys?: unknown[][];
}

/**
 * Rename a quote option (e.g. "Tier 1 — Luxury"). Blank falls back to the
 * auto label built from hotels, nights and pax.
 */
export const QuoteLabelInlineEdit = ({
  quotationId,
  value,
  autoLabel,
  invalidateKeys = [],
}: QuoteLabelInlineEditProps) => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || "");

  const saveMutation = useMutation({
    mutationFn: async (next: string) => {
      const { error } = await supabase
        .from("quotations")
        .update({ quote_label: next.trim() || null } as any)
        .eq("id", quotationId);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidateKeys.forEach((key) => queryClient.invalidateQueries({ queryKey: key as any }));
      setEditing(false);
      toast.success("Quote name updated");
    },
    onError: (err: any) => toast.error(err.message || "Failed to update"),
  });

  if (editing) {
    return (
      <div className="mt-1 flex items-center gap-2">
        <Input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={autoLabel || "e.g. Tier 1 — Luxury"}
          className="h-8 max-w-sm text-sm"
        />
        <Button
          size="sm"
          variant="ghost"
          onClick={() => saveMutation.mutate(draft)}
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
        </Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)} disabled={saveMutation.isPending}>
          <X className="h-4 w-4" />
        </Button>
      </div>
    );
  }

  const manual = (value || "").trim();

  return (
    <div className="mt-0.5">
      <button
        type="button"
        onClick={() => {
          setDraft(manual);
          setEditing(true);
        }}
        className="inline-flex items-start gap-1 text-left text-sm text-foreground/90 hover:text-foreground"
      >
        <Pencil className="h-3 w-3 mt-1 flex-shrink-0 text-muted-foreground" />
        <span>{manual || autoLabel || "Name this option"}</span>
      </button>
      {manual && autoLabel && (
        <p className="text-xs text-muted-foreground ml-4">{autoLabel}</p>
      )}
    </div>
  );
};
